import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import Button from "../../components/flat/ui/Button";
import AuthPasswordLayout, {
  ButtonText,
  Container,
  Div,
  LinkButton,
  ResendLink,
  Span,
  StyledBadge,
  StyledInputOtp,
  SuccessMessage,
  Subtitle,
  Title,
} from "../../modules/Auth/components/AuthPasswordLayout";
import {
  resendFirstAccessCode,
  verifyFirstAccessCode,
} from "./firstAccessCrud";
import {
  trackFirstAccess2faCodeSent,
  trackFirstAccess2faCodeVerified,
} from "./trackEvents";
import * as auth from "../../modules/Auth/_redux/authRedux";
import { FIRST_ACCESS_ROUTES } from "./routes";

interface LocationState {
  email: string;
  tempToken: string;
}

interface PageProps {
  login: (authToken: string) => void;
  setUserLoginId: (loginId: number | null) => void;
}

function VerifyStep({ login, setUserLoginId }: PageProps) {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const [email, setEmail] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState({ isError: false, message: "" });
  const [successMessage, setSuccessMessage] = useState("");
  const [otpInputKey, setOtpInputKey] = useState(0);
  const [retryAt, setRetryAt] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState<string | null>(null);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    const fragmentParams = new URLSearchParams(location.hash.replace(/^#/, ""));
    const fragmentToken = fragmentParams.get("token");
    const fragmentEmail = fragmentParams.get("email");

    if (fragmentToken && fragmentEmail) {
      setEmail(fragmentEmail);
      setTempToken(fragmentToken);
      history.replace(FIRST_ACCESS_ROUTES.VERIFICATION, {
        email: fragmentEmail,
        tempToken: fragmentToken,
      });
      return;
    }

    if (location.state?.email && location.state?.tempToken) {
      setEmail(location.state.email);
      setTempToken(location.state.tempToken);
      return;
    }

    history.replace(FIRST_ACCESS_ROUTES.BASE);
  }, [location, history]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    const updateCountdown = () => {
      if (!retryAt) return;

      const difference = retryAt.getTime() - Date.now();
      if (difference > 0) {
        const totalSeconds = Math.ceil(difference / 1000);
        setCountdown(
          `Aguarde ${totalSeconds} segundo${totalSeconds !== 1 ? "s" : ""}`
        );
      } else {
        setCountdown(null);
        setRetryAt(null);
        if (timer) clearInterval(timer);
      }
    };

    if (retryAt) {
      updateCountdown();
      timer = setInterval(updateCountdown, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [retryAt]);

  const handleOtpComplete = (code: string) => {
    setOtpCode(code);
    setError({ isError: false, message: "" });
    setSuccessMessage("");
    handleSubmit(code);
  };

  const handleSubmit = async (codeToSubmit: string) => {
    if (isSubmittingRef.current || isLoading) {
      return;
    }

    setError({ isError: false, message: "" });
    setSuccessMessage("");

    if (codeToSubmit.length !== 6) {
      setError({
        isError: true,
        message: "Por favor, preencha todos os dígitos do código.",
      });
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);

    try {
      const { data: responseData } = await verifyFirstAccessCode(
        codeToSubmit,
        tempToken
      );

      if (responseData.status === "success") {
        trackFirstAccess2faCodeVerified({ email });

        if (responseData.data?.accessToken) {
          setUserLoginId(responseData.data.userLoginId ?? null);
          login(responseData.data.accessToken);
          history.push("/");
          return;
        }

        if (responseData.data?.tempToken) {
          history.push(FIRST_ACCESS_ROUTES.CREATE_PASSWORD, {
            email: responseData.data.email || email,
            tempToken: responseData.data.tempToken,
          });
          return;
        }
      }

      setError({
        isError: true,
        message:
          responseData.message || "A verificação falhou. Tente novamente.",
      });
      setOtpInputKey(prevKey => prevKey + 1);
    } catch (err) {
      setError({
        isError: true,
        message:
          err?.response?.data?.message ||
          "Código inválido! Confirme seu e-mail e tente novamente.",
      });
      setOtpInputKey(prevKey => prevKey + 1);
    } finally {
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError({ isError: false, message: "" });
    setSuccessMessage("");

    try {
      await resendFirstAccessCode(tempToken);
      trackFirstAccess2faCodeSent({ email });
      setSuccessMessage("Novo código enviado com sucesso!");
      setRetryAt(null);
    } catch (error) {
      if (error?.response?.data?.data?.retryAt) {
        setRetryAt(new Date(error.response.data.data.retryAt));
      }

      setError({
        isError: true,
        message:
          error?.response?.data?.message ??
          "Falha ao reenviar o código. Tente novamente.",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthPasswordLayout variant="banner">
      <Container>
        <Div>
          <Title>Enviamos um código para seu e-mail</Title>
          <Subtitle>
            Insira o código de verificação enviado para o seu e-mail. Caso
            não encontre, verifique sua pasta de spam.
          </Subtitle>
          <StyledBadge content={email} />
        </Div>

        <StyledInputOtp
          key={otpInputKey}
          onComplete={handleOtpComplete}
          disabled={isLoading || isResending}
          isError={error.isError}
          errorMessage={error.message}
        />

        {successMessage && (
          <SuccessMessage>{successMessage}</SuccessMessage>
        )}

        <Button
          width="100%"
          padding="10px 40px"
          borderRadius="5px"
          hoverBackgroundColor="#007656"
          onClick={() => handleSubmit(otpCode)}
          disabled={isLoading || isResending}
        >
          <ButtonText>
            {isLoading ? (
              <TailSpin
                visible
                height="20"
                width="20"
                color="#ffffff"
                ariaLabel="loading"
              />
            ) : (
              "Continuar"
            )}
          </ButtonText>
        </Button>

        <Div>
          <Span>
            Precisa de um novo código?{" "}
            <ResendLink
              isDisabled={isLoading || isResending || !!retryAt}
              onClick={event => {
                if (isLoading || isResending || retryAt) {
                  event.preventDefault();
                  return;
                }
                handleResendCode();
              }}
            >
              {countdown
                ? countdown
                : isResending
                ? "Reenviando..."
                : "Reenviar código"}
            </ResendLink>
          </Span>
          <LinkButton
            type="button"
            onClick={() =>
              history.push(FIRST_ACCESS_ROUTES.BASE, { email })
            }
            style={{ marginTop: 8 }}
          >
            Não é meu e-mail!
          </LinkButton>
        </Div>
      </Container>
    </AuthPasswordLayout>
  );
}

export { VerifyStep as UnconnectedVerifyStep };
export default connect(null, auth.actions)(VerifyStep);
